import { create } from 'zustand'
import type {
  CallHistoryRecord,
  CallHistoryStore,
  CallHistoryFetchParams,
  Pagination,
} from '../types'

/**
 * 通話履歴の状態管理ストア
 * WebSocketからの部分更新をサポート
 */
export const useCallHistoryStore = create<CallHistoryStore>((set, get) => ({
  // 通話履歴データ（callHistoryIdをキーとしたMap的な配列）
  callHistory: [],

  // ローディング状態
  loading: false,

  // エラー
  error: null,

  // ページネーション
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  },

  // 最後に取得したフィルタ条件（再取得時に使用）
  lastFetchParams: null,

  // 通話履歴一覧をセット（全置換）
  // originalDirectionの保護: 既存ストアの値を優先し、欠落時はフォールバック
  setCallHistory: (callHistory: CallHistoryRecord[]) => {
    const existingMap = new Map<string, string>()
    const currentHistory = get().callHistory
    for (const item of currentHistory) {
      if (item.callHistoryId && item.originalDirection) {
        existingMap.set(item.callHistoryId, item.originalDirection)
      }
    }

    const safeCallHistory = callHistory.map((record) => {
      const existingOrigDir = existingMap.get(record.callHistoryId)

      if (!record.originalDirection) {
        // APIからoriginalDirectionが欠落 → 既存ストアの値かdirectionでフォールバック
        const fallback = existingOrigDir || record.direction || 'inbound'
        console.warn('[CallHistoryStore] originalDirection欠落:', record.callHistoryId, 'direction=', record.direction, '→ fallback=', fallback)
        return { ...record, originalDirection: fallback as CallHistoryRecord['originalDirection'] }
      }

      if (existingOrigDir && existingOrigDir !== record.originalDirection) {
        // APIの値がストアと異なる → ストアの値を優先（不正な変更を防止）
        console.warn('[CallHistoryStore] originalDirection変更を検出:', record.callHistoryId,
          'store=', existingOrigDir, 'api=', record.originalDirection, '→ storeの値を維持')
        return { ...record, originalDirection: existingOrigDir as CallHistoryRecord['originalDirection'] }
      }

      return record
    })
    set({ callHistory: safeCallHistory })
  },

  // ローディング状態をセット
  setLoading: (loading: boolean) => set({ loading }),

  // エラーをセット
  setError: (error: string | null) => set({ error }),

  // ページネーションをセット
  setPagination: (pagination: Pagination) => set({ pagination }),

  // 最後のフェッチパラメータを保存
  setLastFetchParams: (params: CallHistoryFetchParams) => set({ lastFetchParams: params }),

  /**
   * 特定の通話履歴レコードを更新（部分更新）
   * WebSocketからの更新イベントで使用
   */
  updateCallHistoryRecord: (callHistoryId: string, updateData: Partial<CallHistoryRecord>) => {
    set((state) => {
      const index = state.callHistory.findIndex(
        (call) => call.callHistoryId === callHistoryId
      )

      if (index === -1) {
        console.log('[CallHistoryStore] updateRecord: レコード未検出:', callHistoryId)
        return state
      }

      // direction/originalDirectionは通話開始時に決定され、後から変更されるべきではないため除外
      const { direction, originalDirection, ...safeUpdateData } = updateData

      if (direction || originalDirection) {
        console.warn('[CallHistoryStore] updateRecord: direction除外:', callHistoryId,
          'stripped direction=', direction, 'originalDirection=', originalDirection,
          'existing=', state.callHistory[index].originalDirection)
      }

      const newCallHistory = [...state.callHistory]
      newCallHistory[index] = {
        ...newCallHistory[index],
        ...safeUpdateData,
        // 更新日時を最新に
        updatedAt: updateData.updatedAt || new Date().toISOString()
      }

      console.log('[CallHistoryStore] updateRecord:', callHistoryId,
        'originalDirection維持=', newCallHistory[index].originalDirection,
        'updateKeys=', Object.keys(safeUpdateData))

      return { callHistory: newCallHistory }
    })
  },

  /**
   * 新しい通話履歴レコードを追加（先頭に挿入）
   * 着信時にWebSocketから受信した場合に使用
   */
  addCallHistoryRecord: (record: CallHistoryRecord) => {
    const state = get()

    console.log('[CallHistoryStore] addRecord:', record.callHistoryId,
      'direction=', record.direction, 'originalDirection=', record.originalDirection)

    // 重複チェック
    const existingIndex = state.callHistory.findIndex(
      (call) => call.callHistoryId === record.callHistoryId
    )

    if (existingIndex !== -1) {
      // 既存の場合は更新（direction, originalDirectionは保護）
      const { direction, originalDirection, ...safeRecord } = record
      const existing = state.callHistory[existingIndex]
      console.log('[CallHistoryStore] addRecord: 既存レコード更新:', record.callHistoryId,
        'existing.originalDirection=', existing.originalDirection,
        'incoming.originalDirection=', originalDirection, '→ 既存値を維持')
      const newCallHistory = [...state.callHistory]
      newCallHistory[existingIndex] = {
        ...existing,
        ...safeRecord,
        direction: existing.direction,
        originalDirection: existing.originalDirection,
        updatedAt: record.updatedAt || new Date().toISOString()
      }
      set({ callHistory: newCallHistory })
      return
    }

    // 新規追加（originalDirectionがない場合はdirectionからフォールバック）
    const safeRecord: CallHistoryRecord = {
      ...record,
      originalDirection: record.originalDirection || record.direction || 'inbound'
    }
    console.log('[CallHistoryStore] addRecord: 新規追加:', record.callHistoryId,
      'originalDirection=', safeRecord.originalDirection)
    const newCallHistory = [safeRecord, ...state.callHistory]

    // ページネーションのtotalを更新
    const newPagination = {
      ...state.pagination,
      total: state.pagination.total + 1
    }

    set({
      callHistory: newCallHistory,
      pagination: newPagination
    })
  },

  /**
   * 特定のフィールドだけを更新
   * 例: ステータスだけ、担当者だけなど
   */
  patchCallHistoryField: (callHistoryId: string, fieldName: string, value: unknown) => {
    // direction/originalDirectionの変更は許可しない
    if (fieldName === 'direction' || fieldName === 'originalDirection') {
      console.warn('[CallHistoryStore] direction/originalDirectionの変更は許可されていません:', fieldName)
      return
    }

    set((state) => {
      const index = state.callHistory.findIndex(
        (call) => call.callHistoryId === callHistoryId
      )

      if (index === -1) return state

      const newCallHistory = [...state.callHistory]
      newCallHistory[index] = {
        ...newCallHistory[index],
        [fieldName]: value,
        updatedAt: new Date().toISOString()
      }

      return { callHistory: newCallHistory }
    })
  },

  /**
   * 通話履歴レコードを削除
   */
  removeCallHistoryRecord: (callHistoryId: string) => {
    set((state) => {
      const newCallHistory = state.callHistory.filter(
        (call) => call.callHistoryId !== callHistoryId
      )

      const newPagination = {
        ...state.pagination,
        total: Math.max(0, state.pagination.total - 1)
      }

      return {
        callHistory: newCallHistory,
        pagination: newPagination
      }
    })
  },

  /**
   * ストアをリセット
   */
  reset: () => set({
    callHistory: [],
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    },
    lastFetchParams: null
  })
}))
